/**
 * Pure rule-based resume scoring.
 * Input: raw text extracted from a resume file.
 * Output: { score: 0-100, checks: [{key, label, pass, weight, hint}] }
 */

const CONTACT_REGEX = /(\+?\d[\d\s()-]{7,}\d)|([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/i;
const LINKEDIN_REGEX = /linkedin\.com\/in\/[a-z0-9-]+/i;
const NUMBER_ACHIEVEMENT_REGEX = /\b\d{1,3}(?:[.,]\d+)?\s?(%|x|\+|years?|yrs?|months?|days?|weeks?|hours?|usd|inr|lakhs?|crores?|million|billion|k|m|b)\b/i;
const ACTION_VERBS = [
  "led", "managed", "developed", "designed", "built", "created", "launched", "delivered",
  "improved", "reduced", "increased", "optimized", "optimised", "implemented", "drove",
  "owned", "shipped", "scaled", "negotiated", "executed", "spearheaded", "established",
  "authored", "validated", "presented", "trained", "mentored",
];
const SECTION_HEADINGS = ["experience", "education", "skills", "summary", "objective", "projects", "certifications", "achievements"];

const PHARMA_KEYWORDS = [
  "pharma", "formulation", "clinical", "regulatory", "gmp", "fda", "ich",
  "qa", "qc", "validation", "manufacturing", "r&d", "research", "trial",
  "ctd", "andA", "ema", "biotech", "api", "compliance", "stability",
];

const wordCount = (s) => (s.match(/\S+/g) || []).length;

export function analyzeResumeText(text) {
  const t = (text || "").trim();
  const lower = t.toLowerCase();
  const wc = wordCount(t);

  const hasContact = CONTACT_REGEX.test(t);
  const hasLinkedIn = LINKEDIN_REGEX.test(t);
  const hasSummary = /\b(summary|profile|objective)\b/i.test(t);
  const sectionsPresent = SECTION_HEADINGS.filter((s) => new RegExp(`\\b${s}\\b`, "i").test(t));
  const goodLength = wc >= 250 && wc <= 1100;
  const okLength = wc >= 150 && wc <= 1500;
  const hasNumbers = NUMBER_ACHIEVEMENT_REGEX.test(t);
  const actionVerbsFound = ACTION_VERBS.filter((v) => new RegExp(`\\b${v}\\b`, "i").test(t));
  const hasActionVerbs = actionVerbsFound.length >= 3;
  const pharmaMatches = PHARMA_KEYWORDS.filter((k) => lower.includes(k.toLowerCase()));
  const hasPharmaContext = pharmaMatches.length >= 2;
  const hasBulletPoints = /(^|\n)\s*[-•·●]\s+/.test(t);
  const hasDates = /\b(19|20)\d{2}\b/.test(t);

  const checks = [
    { key: "contact", label: "Contact info present", pass: hasContact, weight: 10, hint: "Add email and/or phone at the top." },
    { key: "linkedin", label: "LinkedIn profile linked", pass: hasLinkedIn, weight: 5, hint: "Add your linkedin.com/in/... URL." },
    { key: "summary", label: "Summary / profile section", pass: hasSummary, weight: 10, hint: "Open with a 2-3 line professional summary." },
    { key: "sections", label: `Core sections (${sectionsPresent.length}/4)`, pass: sectionsPresent.length >= 4, weight: 10, hint: "Include Experience, Education, Skills and one of Summary/Projects." },
    { key: "length", label: "Right length", pass: goodLength, weight: 10, hint: okLength ? "A bit long/short. Aim for 1-2 pages (~250-1100 words)." : "Resume seems too short or too long. Aim for 1-2 pages." },
    { key: "numbers", label: "Quantified achievements", pass: hasNumbers, weight: 15, hint: "Add at least one metric (%, $, x, years, headcount, etc.)." },
    { key: "action", label: "Action verbs used", pass: hasActionVerbs, weight: 10, hint: "Lead bullets with verbs like Led, Built, Reduced, Delivered." },
    { key: "bullets", label: "Bullet-pointed experience", pass: hasBulletPoints, weight: 5, hint: "Use bullet points (-, •) for readability." },
    { key: "dates", label: "Dates included", pass: hasDates, weight: 5, hint: "Add years (e.g. 2022 - Present) for every role." },
    { key: "pharma", label: "Pharma-domain keywords", pass: hasPharmaContext, weight: 15, hint: "Mention pharma context — GMP, ICH, formulation, clinical, regulatory, etc." },
    { key: "ats", label: "Clean text (ATS-readable)", pass: wc > 80, weight: 5, hint: "PDF/DOCX should contain real text, not images of text." },
  ];

  const totalWeight = checks.reduce((a, c) => a + c.weight, 0);
  const gained = checks.filter((c) => c.pass).reduce((a, c) => a + c.weight, 0);
  const score = Math.round((gained / totalWeight) * 100);

  return {
    score,
    word_count: wc,
    checks,
    matched: {
      action_verbs: actionVerbsFound.slice(0, 8),
      pharma_keywords: pharmaMatches,
      sections: sectionsPresent,
    },
  };
}
